import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import useStore from "../../lib/store";

/**
 * Global keyboard handler.
 *
 * Always-active:  Ctrl+P → Telescope search
 * Opt-in (keyboardNav):  Space/:/letters/j/k/gg/G → Vim-style navigation
 */
const KeyboardHandler = () => {
  const navigate = useNavigate();
  const pendingRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    const onKeyDown = (e) => {
      const s = useStore.getState();

      // ---- Always: Ctrl+P telescopes search ----
      if (e.key === "/") {
        e.preventDefault();
        if (s.searchOpen) s.closeSearch();
        else s.openSearch();
        return;
      }

      // ---- Always: Escape closes overlays ----
      if (
        e.key === "Escape" &&
        (s.searchOpen ||
          s.whichKeyVisible ||
          s.commandModeOpen ||
          s.floatingWindow)
      ) {
        e.preventDefault();
        s.closeAllOverlays();
        return;
      }

      // ---- Only when keyboard nav is enabled ----
      if (!s.keyboardNav) return;
      if (s.mode !== "NORMAL") return;
      // Don't interfere with overlays
      if (
        s.searchOpen ||
        s.whichKeyVisible ||
        s.commandModeOpen ||
        s.floatingWindow
      )
        return;

      const key = e.key;

      // gg chord — scroll to top
      if (key === "g") {
        if (pendingRef.current === "g") {
          pendingRef.current = null;
          if (timerRef.current) clearTimeout(timerRef.current);
          scrollEditor("top");
        } else {
          pendingRef.current = "g";
          if (timerRef.current) clearTimeout(timerRef.current);
          timerRef.current = setTimeout(() => {
            pendingRef.current = null;
          }, 500);
        }
        return;
      }
      if (key === "G") {
        scrollEditor("bottom");
        return;
      }

      // j/k — scroll
      if (key === "j" || key === "ArrowDown") {
        e.preventDefault();
        scrollEditor("down", 56);
        return;
      }
      if (key === "k" || key === "ArrowUp") {
        e.preventDefault();
        scrollEditor("up", 56);
        return;
      }

      // Space — which-key
      if (key === " ") {
        e.preventDefault();
        s.openWhichKey();
        return;
      }
      // : — command mode
      if (key === ":") {
        s.openCommandMode();
        return;
      }
      // ? — which-key
      if (key === "?") {
        e.preventDefault();
        s.openWhichKey();
        return;
      }

      // t - toggle theme
      if (key === "t") {
        e.preventDefault();
        s.toggleTheme();
        return;
      }

      // Single-letter page navigation
      const navMap = {
        d: "/",
        a: "/about",
        p: "/projects",
        r: "/resume",
        c: "/contact",
      };
      if (navMap[key]) {
        navigate(navMap[key]);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [navigate]);

  return null;
};

function scrollEditor(dir, amount) {
  const el = document.querySelector(".editor");
  if (!el) return;
  switch (dir) {
    case "top":
      el.scrollTop = 0;
      break;
    case "bottom":
      el.scrollTop = el.scrollHeight;
      break;
    case "down":
      el.scrollTop += amount ?? 56;
      break;
    case "up":
      el.scrollTop -= amount ?? 56;
      break;
  }
}

export default KeyboardHandler;
